import _defineProperty from '@babel/runtime-corejs3/helpers/defineProperty';
import _bindInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/bind';
import classNames from 'classnames/bind';
import React from 'react';
import FooterInfo from './FooterInfo.js';
import FooterQuickMenu from './FooterQuickMenu.js';
import NpayDownloadButton from './NpayDownloadButton.js';
import COLOR from '../../foundation/color.js';
import style from './Footer.module.scss.js';

var cx = _bindInstanceProperty(classNames).call(classNames, style);
/**
 * 네이버 파이낸셜 공통 푸터
 *
 * ### Overwrite
 * overwrite은 메뉴를 추가 또는 삭제하거나 전체를 수정하는 기능입니다.
 *
 * - 퀵메뉴
 *
 * #### `overwriteMenuList`
 *
 * 퀵메뉴를 overwrite합니다.
 *
 * ```jsx
 * <Footer
 *     overwriteMenuList={[
 *         // overwrite 시, 로그인, 로그아웃, 전체서비스는 isLogin, isMobile 분기처리가 필요합니다.
 *         {
 *             title: isLogin ? FOOTER_QUICK_MENU_TITLE.LOGOUT : FOOTER_QUICK_MENU_TITLE.LOGIN,
 *             href: '#',
 *             onClickLogin: () => {
 *                 alert('클릭 로그인');
 *             },
 *             onClickLogout: () => {
 *                 alert('클릭 로그아웃');
 *             },
 *         },
 *         {
 *             title: FOOTER_QUICK_MENU_TITLE.ALL_SERVICE,
 *             href: isMobile ? URL.ALL_SERVICES.MOBILE : URL.ALL_SERVICES.PC,
 *         },
 *         FOOTER_QUICK_MENU_ITEM_MAP.stock['약관 및 정책'],
 *     ]}
 *     {...footerProps}
 * />
 * ```
 *
 * ### Override
 * override는 Footer에서 제공하는 기본 설정을 수정하는 기능입니다.
 * override한 설정이 다른 설정보다 더 높은 우선순위를 가지며 먼저 적용됩니다.
 *
 * - 퀵메뉴 일부 필드
 * - 하단 정보영역
 *
 * #### `overrideMenuItem`
 *
 * 퀵메뉴의 일부 field를 override합니다.
 *
 * ```jsx
 * <Footer
 *     overrideMenuItem={{
 *         [FOOTER_QUICK_MENU_TITLE.LOGIN]: {
 *             onClickLogin: (e) => { // 로그인 클릭이벤트 핸들러
 *                 e.preventDefault();
 *                 window.alert('onClickLogin');
 *             },
 *         },
 *         [FOOTER_QUICK_MENU_TITLE.LOGOUT]: {
 *             onClickLogout: (e) => { // 로그아웃 클릭이벤트 핸들러
 *                 e.preventDefault();
 *                 window.alert('onClickLogout');
 *             },
 *         },
 *     }}
 *     {...footerProps}
 * />
 * ```
 *
 * #### `overrideInfo`
 *
 * Footer 하단 정보의 일부 설정을 override합니다.
 * - **NFBusinessInfo** : 네이버파이낸셜(주) 사업자 정보의 설정을 변경합니다. `button 태그의 attribute`
 * - **description** : 새로운 description으로 변경합니다. `ReactNode`
 * - **logo** : 네이버 파이낸셜 로고의 설정을 변경합니다. `a 태그의 attribute`
 * - **cs** : 1:1 문의 바로가기 설정을 변경합니다. `a 태그의 attribute`
 *
 * ```jsx
 * <Footer
 *     overrideInfo= {{
 *         NFBusinessInfo:{
 *             'data-nclicks': '네이버파이낸셜(주) 사업자 정보 클릭코드',
 *         }
 *         description: <>overrideInfo.description을 넘기면 FooterInfo Description을 변경할 수 있어요.</>,
 *         logo: {
 *             'data-nclicks': '로고 클릭코드',
 *         },
 *         cs: {
 *             'data-nclicks': '1:1 문의 바로가기 클릭코드',
 *         },
 *     }}
 *     {...footerProps}
 * />
 * ```
 *
 */
function Footer(_ref) {
  var _ref$bgColor = _ref.bgColor,
    bgColor = _ref$bgColor === void 0 ? COLOR.gray150.name : _ref$bgColor,
    type = _ref.type,
    isLogin = _ref.isLogin,
    isMobile = _ref.isMobile,
    overwriteMenuList = _ref.overwriteMenuList,
    overrideMenuItem = _ref.overrideMenuItem,
    overrideInfo = _ref.overrideInfo,
    _ref$widthType = _ref.widthType,
    widthType = _ref$widthType === void 0 ? 'full' : _ref$widthType,
    _ref$borderType = _ref.borderType,
    borderType = _ref$borderType === void 0 ? 'none' : _ref$borderType,
    _ref$isPaddingInside = _ref.isPaddingInside,
    isPaddingInside = _ref$isPaddingInside === void 0 ? false : _ref$isPaddingInside,
    _ref$openNewWindow = _ref.openNewWindow,
    openNewWindow = _ref$openNewWindow === void 0 ? false : _ref$openNewWindow,
    _ref$showBottomMargin = _ref.showBottomMargin,
    showBottomMargin = _ref$showBottomMargin === void 0 ? false : _ref$showBottomMargin,
    npayDownloadButtonProps = _ref.npayDownloadButtonProps;
  // 푸터 배경 흰색인 경우: 로고 gray400, 텍스트 gray 600
  // 푸터 배경 흰색이 아닌 경우: 로고 gray500, 텍스트 gray700
  var logoColor = bgColor === COLOR.white.name ? COLOR.gray400.name : COLOR.gray500.name;
  var textColor = bgColor === COLOR.white.name ? COLOR.gray600.name : COLOR.gray700.name;
  var showNpayDownloadButton = !(npayDownloadButtonProps !== null && npayDownloadButtonProps !== void 0 && npayDownloadButtonProps.isPayApp) && ((npayDownloadButtonProps === null || npayDownloadButtonProps === void 0 ? void 0 : npayDownloadButtonProps.isAndroid) || (npayDownloadButtonProps === null || npayDownloadButtonProps === void 0 ? void 0 : npayDownloadButtonProps.isIOS));
  return /*#__PURE__*/React.createElement("div", {
    className: cx('article', "type-".concat(bgColor), widthType !== 'full' ? _defineProperty({}, "width-type-".concat(widthType), widthType) : undefined, borderType !== 'none' ? _defineProperty({}, "border-type-".concat(borderType), borderType) : undefined, {
      'is-padding-inside': isPaddingInside,
      'is-bottom-margin': showBottomMargin
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('inner')
  }, /*#__PURE__*/React.createElement(FooterQuickMenu, {
    type: type,
    isLogin: isLogin,
    isMobile: isMobile,
    overwriteMenuList: overwriteMenuList,
    overrideMenuItem: overrideMenuItem,
    openNewWindow: openNewWindow
  }), /*#__PURE__*/React.createElement(FooterInfo, {
    type: type,
    logoColor: logoColor,
    textColor: textColor,
    overrideInfo: overrideInfo,
    openNewWindow: openNewWindow
  }), showNpayDownloadButton && /*#__PURE__*/React.createElement(NpayDownloadButton, {
    npayDownloadButtonProps: npayDownloadButtonProps
  })));
}

export { Footer as default };
